/**
 * Org-scoped API client. Covers CRUD on deploy targets and asset
 * origins (the credentialed configs the project links to), plus the
 * "import a localStorage project" endpoint.
 *
 * Project CRUD lives on `ApiStore` (in `stores/api.ts`) because that's
 * the surface the editor talks to. Org-level resources are managed
 * separately — admin-style screens consume this module directly.
 */

import type { AssetOriginConfig, DeployTargetConfig, JsonSchema, JsonValue } from '@bliksys/shared';

import { UnauthenticatedError } from '../stores/types.js';

const DEFAULT_BASE = 'http://localhost:3001';

export interface DeployTargetSummary {
  id: string;
  name: string;
  kind: 'github' | 'webhook' | 'cloudflareKV';
  createdAt: string;
  updatedAt: string;
}

export interface AssetOriginSummary {
  id: string;
  name: string;
  kind: 's3';
  createdAt: string;
  updatedAt: string;
}

export type OrgRole = 'owner' | 'admin' | 'member';
export type ProjectRole = 'maintainer' | 'developer' | 'writer' | 'viewer';

export interface MemberRow {
  userId: string;
  email: string;
  name: string | null;
  role: OrgRole;
  joinedAt: string;
}

export interface ProjectMemberRow {
  userId: string;
  email: string;
  name: string | null;
  role: ProjectRole;
  addedAt: string;
}

export interface InvitationRow {
  id: string;
  email: string;
  orgRole: OrgRole;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
}

export interface AuditEvent {
  id: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  meta: unknown;
  at: string;
  ip: string | null;
  actor: { id: string; email: string; name: string | null } | null;
}

export interface InvoiceSummary {
  number: string | null;
  url: string | null;
  /** Total in the currency's minor units (e.g. cents). */
  total: number;
  currency: string;
  at: string;
}

export interface PlanInfo {
  slug: string;
  name: string;
  description: string;
  priceLabel: string;
  features: string[];
}

export interface AvailablePlan extends PlanInfo {
  /** False until a Stripe price id is configured for this plan slug. */
  purchasable: boolean;
}

export interface BillingStatus {
  plan: string;
  /** Display metadata for the org's current plan. */
  planDisplay: PlanInfo;
  status: string;
  /** Whether Stripe is configured on the server (gates checkout/portal). */
  configured: boolean;
  /** Whether a Stripe customer exists yet (gates the billing portal). */
  portalAvailable: boolean;
  /** Plan slugs available for checkout (legacy; see availablePlans). */
  plans: string[];
  /** Rich upgrade options with display metadata + purchasable flag. */
  availablePlans: AvailablePlan[];
  subscription: {
    plan: string;
    status: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  invoices: InvoiceSummary[];
}

/** Server returns scrubbed config — secret fields replaced with '***'. */
export interface DeployTargetDetail extends DeployTargetSummary {
  config: DeployTargetConfig;
}

export interface AssetOriginDetail extends AssetOriginSummary {
  config: AssetOriginConfig;
}

export interface OrgApiOptions {
  baseUrl?: string;
  orgSlug: string;
}

export class OrgApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(args: { status: number; code: string; message: string }) {
    super(args.message);
    this.name = 'OrgApiError';
    this.status = args.status;
    this.code = args.code;
  }
}

export function orgApi(opts: OrgApiOptions): {
  listDeployTargets(): Promise<DeployTargetSummary[]>;
  getDeployTarget(id: string): Promise<DeployTargetDetail>;
  createDeployTarget(input: {
    name: string;
    config: DeployTargetConfig;
  }): Promise<DeployTargetSummary>;
  updateDeployTarget(
    id: string,
    patch: { name?: string; config?: DeployTargetConfig },
  ): Promise<DeployTargetSummary>;
  deleteDeployTarget(id: string): Promise<void>;

  /**
   * Org-wide allow-list of webhook deploy destination hostnames. Webhook
   * targets can't be created or delivered unless their host is listed.
   * Reading is open to members; replacing the list is owner-only (403).
   */
  getWebhookAllowedHosts(): Promise<string[]>;
  setWebhookAllowedHosts(hosts: string[]): Promise<string[]>;

  listAssetOrigins(): Promise<AssetOriginSummary[]>;
  getAssetOrigin(id: string): Promise<AssetOriginDetail>;
  createAssetOrigin(input: {
    name: string;
    config: AssetOriginConfig;
  }): Promise<AssetOriginSummary>;
  updateAssetOrigin(
    id: string,
    patch: { name?: string; config?: AssetOriginConfig },
  ): Promise<AssetOriginSummary>;
  deleteAssetOrigin(id: string): Promise<void>;

  importProject(input: {
    name: string;
    schema: JsonSchema;
    data: JsonValue;
  }): Promise<{ id: string }>;

  listMembers(): Promise<MemberRow[]>;
  updateMemberRole(userId: string, role: OrgRole): Promise<void>;
  removeMember(userId: string): Promise<void>;

  listInvitations(): Promise<InvitationRow[]>;
  inviteMember(input: {
    email: string;
    role: OrgRole;
    projectGrants?: { projectId: string; role: ProjectRole }[];
  }): Promise<{ id: string }>;
  revokeInvitation(id: string): Promise<void>;

  /* project members */
  listProjectMembers(projectId: string): Promise<ProjectMemberRow[]>;
  addProjectMember(projectId: string, input: { userId: string; role: ProjectRole }): Promise<void>;
  updateProjectMemberRole(projectId: string, userId: string, role: ProjectRole): Promise<void>;
  removeProjectMember(projectId: string, userId: string): Promise<void>;

  listAuditEvents(opts?: { before?: string; limit?: number }): Promise<AuditEvent[]>;

  getBilling(): Promise<BillingStatus>;
  startCheckout(input: { plan: string }): Promise<{ url: string }>;
  openBillingPortal(): Promise<{ url: string }>;
} {
  const baseUrl = opts.baseUrl ?? import.meta.env.BLIKSYS_API_URL ?? DEFAULT_BASE;
  const root = `${baseUrl}/v1/orgs/${encodeURIComponent(opts.orgSlug)}`;

  async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await fetch(`${root}${path}`, {
      credentials: 'include',
      ...init,
    });
    if (res.status === 401) throw new UnauthenticatedError();
    if (!res.ok) {
      let body: { code?: string; message?: string } = {};
      try {
        body = (await res.json()) as typeof body;
      } catch {
        /* fall through */
      }
      throw new OrgApiError({
        status: res.status,
        code: body.code ?? 'HTTP_ERROR',
        message: body.message ?? res.statusText,
      });
    }
    return (await res.json()) as T;
  }

  function jsonInit(method: string, body: unknown): RequestInit {
    return {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    };
  }

  return {
    /* ---- deploy targets ---- */
    async listDeployTargets() {
      const r = await call<{ deployTargets: DeployTargetSummary[] }>('/deploy-targets');
      return r.deployTargets;
    },
    async getDeployTarget(id) {
      const r = await call<{ deployTarget: DeployTargetDetail }>(
        `/deploy-targets/${encodeURIComponent(id)}`,
      );
      return r.deployTarget;
    },
    async createDeployTarget(input) {
      const r = await call<{ deployTarget: DeployTargetSummary }>(
        '/deploy-targets',
        jsonInit('POST', input),
      );
      return r.deployTarget;
    },
    async updateDeployTarget(id, patch) {
      const r = await call<{ deployTarget: DeployTargetSummary }>(
        `/deploy-targets/${encodeURIComponent(id)}`,
        jsonInit('PATCH', patch),
      );
      return r.deployTarget;
    },
    async getWebhookAllowedHosts() {
      const r = await call<{ hosts: string[] }>('/webhook-allowed-hosts');
      return r.hosts;
    },
    async setWebhookAllowedHosts(hosts) {
      const r = await call<{ hosts: string[] }>(
        '/webhook-allowed-hosts',
        jsonInit('PUT', { hosts }),
      );
      return r.hosts;
    },
    async deleteDeployTarget(id) {
      await call<{ ok: boolean }>(`/deploy-targets/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
    },

    /* ---- asset origins ---- */
    async listAssetOrigins() {
      const r = await call<{ assetOrigins: AssetOriginSummary[] }>('/asset-origins');
      return r.assetOrigins;
    },
    async getAssetOrigin(id) {
      const r = await call<{ assetOrigin: AssetOriginDetail }>(
        `/asset-origins/${encodeURIComponent(id)}`,
      );
      return r.assetOrigin;
    },
    async createAssetOrigin(input) {
      const r = await call<{ assetOrigin: AssetOriginSummary }>(
        '/asset-origins',
        jsonInit('POST', input),
      );
      return r.assetOrigin;
    },
    async updateAssetOrigin(id, patch) {
      const r = await call<{ assetOrigin: AssetOriginSummary }>(
        `/asset-origins/${encodeURIComponent(id)}`,
        jsonInit('PATCH', patch),
      );
      return r.assetOrigin;
    },
    async deleteAssetOrigin(id) {
      await call<{ ok: boolean }>(`/asset-origins/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },

    /* ---- import ---- */
    async importProject(input) {
      const r = await call<{ project: { id: string } }>(
        '/projects/import',
        jsonInit('POST', input),
      );
      return r.project;
    },

    /* ---- members ---- */
    async listMembers() {
      const r = await call<{ members: MemberRow[] }>('/members');
      return r.members;
    },
    async updateMemberRole(userId, role) {
      await call<unknown>(`/members/${encodeURIComponent(userId)}`, jsonInit('PATCH', { role }));
    },
    async removeMember(userId) {
      await call<unknown>(`/members/${encodeURIComponent(userId)}`, { method: 'DELETE' });
    },

    /* ---- invitations ---- */
    async listInvitations() {
      const r = await call<{ invitations: InvitationRow[] }>('/invitations');
      return r.invitations;
    },
    async inviteMember(input) {
      const r = await call<{ invitation: { id: string } }>('/invitations', jsonInit('POST', input));
      return r.invitation;
    },
    async revokeInvitation(id) {
      await call<unknown>(`/invitations/${encodeURIComponent(id)}`, { method: 'DELETE' });
    },

    /* ---- project members ---- */
    async listProjectMembers(projectId) {
      const r = await call<{ members: ProjectMemberRow[] }>(
        `/projects/${encodeURIComponent(projectId)}/members`,
      );
      return r.members;
    },
    async addProjectMember(projectId, input) {
      await call<unknown>(
        `/projects/${encodeURIComponent(projectId)}/members`,
        jsonInit('POST', input),
      );
    },
    async updateProjectMemberRole(projectId, userId, role) {
      await call<unknown>(
        `/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
        jsonInit('PATCH', { role }),
      );
    },
    async removeProjectMember(projectId, userId) {
      await call<unknown>(
        `/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
        { method: 'DELETE' },
      );
    },

    /* ---- audit log ---- */
    async listAuditEvents(opts = {}) {
      const params = new URLSearchParams();
      if (opts.before !== undefined) params.set('before', opts.before);
      if (opts.limit !== undefined) params.set('limit', String(opts.limit));
      const qs = params.toString();
      const r = await call<{ events: AuditEvent[] }>(`/audit-log${qs ? `?${qs}` : ''}`);
      return r.events;
    },

    /* ---- billing ---- */
    async getBilling() {
      const r = await call<{ billing: BillingStatus }>('/billing');
      return r.billing;
    },
    async startCheckout(input) {
      return call<{ url: string }>('/billing/checkout', jsonInit('POST', input));
    },
    async openBillingPortal() {
      return call<{ url: string }>('/billing/portal', jsonInit('POST', {}));
    },
  };
}

export type OrgApi = ReturnType<typeof orgApi>;
